import { ComponentTypes, ITemplateComponent } from "entities/template";
import React, { useMemo } from "react";
import { ComponentBadge } from "../component_badge/component_badge";
import { useComponentsTranslations } from "../../lib/i18n";
import styles from "./component_selection.module.scss";
import { IComponentsConfig, useComponentsConfig } from "../../lib/hooks/useComponentsConfig";

interface IComponentsSelectionProps {
    components: ITemplateComponent[];
    searchInput: string;
    onComponentSelect: (type: ComponentTypes) => void;
}

export const ComponentSelection: React.FC<IComponentsSelectionProps> = React.memo(({
                                                                                       components,
                                                                                       searchInput,
                                                                                       onComponentSelect
                                                                                   }) => {
    const t = useComponentsTranslations();
    const componentsConfig = useComponentsConfig();

    const filterComponentTypes = (type: ComponentTypes, config: IComponentsConfig, search: string): boolean => {
        const componentConfig = config[type];
        const trimmedInput = search.toLowerCase().trim();
        const label = componentConfig.label.toLowerCase().trim();
        const subLabel = componentConfig.subLabel.toLowerCase().trim();
        const filteredByLabel = label.indexOf(trimmedInput) > -1;
        const filteredBySubLabel = subLabel.indexOf(trimmedInput) > -1;

        return filteredByLabel || filteredBySubLabel;
    }

    const isShowSingleComponent = (type: ComponentTypes) => {
        return !components.find((c) => c.type === type);
    }

    const simpleComponents = useMemo(() => {
        const cTypes = [
            ComponentTypes.dateInput,
            ComponentTypes.text,
            ComponentTypes.input,
            ComponentTypes.multiline,
            ComponentTypes.select,
            ComponentTypes.inputWithSelect,
            ComponentTypes.money,
            ComponentTypes.checkbox,
            ComponentTypes.radio,
            ComponentTypes.table,
            ComponentTypes.file
        ]

        return cTypes.filter(type => filterComponentTypes(type, componentsConfig, searchInput))
    }, [searchInput, componentsConfig])

    const functionalComponents = useMemo(() => {
        const cTypes = [
            ComponentTypes.hrPerson,
            ComponentTypes.counterparty,
            ComponentTypes.legalYandex,
            ComponentTypes.saveAndTicket,
            ComponentTypes.saveToExistingTicket,
            ComponentTypes.pdfPassword,
            ComponentTypes.trackerIntegration,
            ComponentTypes.iprocIntegration,
            ComponentTypes.externalForm,
            ComponentTypes.language,
            ComponentTypes.uuid,
            ComponentTypes.barcode,
            ComponentTypes.autonumber,
            ComponentTypes.preview,
            ComponentTypes.massDocGeneration
        ]
        const singleCTypes = [ComponentTypes.pdfPassword, ComponentTypes.externalForm, ComponentTypes.language, ComponentTypes.preview]

        return cTypes.filter(type => filterComponentTypes(type, componentsConfig, searchInput) && (!singleCTypes.includes(type) || isShowSingleComponent(type)))
    }, [components, searchInput, componentsConfig])

    const onBadgeClick = (type: ComponentTypes) => {
        onComponentSelect(type);
    }

    return <div className={styles.container}>
        {simpleComponents.length === 0 && functionalComponents.length === 0 && <div className={styles.notFound}>
            {t("noComponentsFound")}
        </div>}
        {simpleComponents.length > 0 && <>
            <div className={styles.componentsSubheader}>
                {t("simpleComponents")}
            </div>
            <div className={styles.componentsList}>
                {simpleComponents.map(componentType => (
                    <ComponentBadge key={componentType} type={componentType} onClick={onBadgeClick}/>
                ))}
            </div>
        </>}
        {functionalComponents.length > 0 && <>
            <div className={styles.componentsSubheader}>
                {t("functionalComponents")}
            </div>
            <div className={styles.componentsList}>
                {functionalComponents.map(componentType => (
                    <ComponentBadge key={componentType} type={componentType} onClick={onBadgeClick}/>
                ))}
            </div>
        </>}
    </div>;
});
